import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { reportLayoutTokens } from "./tokens";

export type ReportTableColumn<Row> = {
  id: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  // Optional width hint, e.g. 120 or "12%"
  width?: string | number;
  cell: (row: Row, rowIndex: number) => ReactNode;
};

export type ReportDataTableProps<Row> = {
  columns: ReportTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row, index: number) => string | number;
  isLoading?: boolean;
  // Shown when not loading and rows are empty
  emptyMessage?: ReactNode;
  // Default: "small"
  size?: "small" | "medium";
  // Header row background and text color overrides
  tableHeaderBackground?: string;
  tableHeaderTextColor?: string;
  // Row rendered before data rows (e.g. opening balance)
  leadingRow?: ReactNode;
  footer?: ReactNode;
};

// Config-driven report table with custom cell renderers and shared header style.
function ReportDataTable<Row>({
  columns,
  rows,
  getRowId,
  isLoading,
  emptyMessage,
  size = "small",
  tableHeaderBackground = reportLayoutTokens.tableHeaderBg,
  tableHeaderTextColor = reportLayoutTokens.tableHeaderText,
  leadingRow,
  footer,
}: ReportDataTableProps<Row>) {
  return (
    <TableContainer
      sx={{
        maxWidth: "100%",
        borderRadius: 0,
      }}
    >
      <Table size={size} stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                bgcolor: tableHeaderBackground,
                color: tableHeaderTextColor,
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                borderBottom: "1px solid #EAECF0",
                whiteSpace: "nowrap",
                py: 1.25,
              },
            }}
          >
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? "left"}
                width={col.width}
                sx={col.align === "right" ? { pr: 2 } : undefined}
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {leadingRow}
              {!rows || rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    {emptyMessage != null ? (
                      emptyMessage
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow
                    key={getRowId(row, index)}
                    sx={{
                      "&:nth-of-type(even)": { bgcolor: "#FCFCFC" },
                      "&:last-of-type td": { borderBottom: 0 },
                      "& td": {
                        borderColor: "#F0ECE8",
                        fontSize: "0.875rem",
                        color: "#101828",
                        py: 1.5,
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align ?? "left"}>
                        {col.cell(row, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </>
          )}
        </TableBody>
        {footer && <TableFooter>{footer}</TableFooter>}
      </Table>
    </TableContainer>
  );
}

export default ReportDataTable;
